import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT, useLang } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { bucketLabel, countText, moneyText, noteLines, symbol } from './reportFormat';

// The pieces every Agent Reports screen is made of: the page frame (title, back arrow, period pills), cards, the big figure with its chart, notes, bars and messages.
// Leo's look: warm paper background, white cards with a thin border, Spectral for the figures, rust accent.

const PAD = { wide: '32px 48px 56px', laptop: '32px 40px 56px', tablet: '28px 32px 48px', phone: '20px 16px 104px' };

export function ReportPage({ title, backTo = null, backLabel = null, pills = null, children, testid = 'report-page' }) {
  const t = useT();
  const vk = useViewportKind();
  const phone = vk === 'phone';
  return (
    <main data-testid={testid} style={{ flexGrow: 1, boxSizing: 'border-box', padding: PAD[vk], display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1240, width: '100%', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {backTo && (
            <Link to={backTo} aria-label={backLabel || t('reports.back')} data-testid="report-back" style={{ width: 40, height: 40, borderRadius: 20, border: `1px solid ${L.border}`, background: L.card, color: L.text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: 20, flexShrink: 0 }}>
              <span aria-hidden="true">←</span>
            </Link>
          )}
          <h1 style={{ margin: 0, fontFamily: L.serif, fontSize: phone ? 26 : 32, fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</h1>
        </div>
        {pills}
      </div>
      {children}
    </main>
  );
}

const pillStyle = (on) => ({
  height: 40, padding: '0 16px', borderRadius: 20, font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  background: on ? L.accent : '#FFFFFF', color: on ? '#FFFFFF' : L.text, border: `1px solid ${on ? L.accent : L.border}`,
});

// kinds: the pills of the screen (e.g. ['this_week', 'this_month', 'this_year', 'custom']); value: { kind, from?, to? }
export function PeriodPills({ kinds, value, onChange }) {
  const t = useT();
  const [open, setOpen] = useState(value.kind === 'custom');
  const [from, setFrom] = useState(value.from || '');
  const [to, setTo] = useState(value.to || '');
  const bad = from && to && from > to;
  return (
    <div data-testid="period-pills" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      {kinds.map((k) => (
        <button key={k} type="button" aria-pressed={value.kind === k} data-testid={`pill-${k}`} style={pillStyle(value.kind === k)}
          onClick={() => { if (k === 'custom') { setOpen(true); if (from && to && !bad) onChange({ kind: 'custom', from, to }); } else { setOpen(false); onChange({ kind: k }); } }}>
          {t(`reports.period.${k}`)}
        </button>
      ))}
      {open && (
        <form data-testid="period-custom" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }} onSubmit={(e) => { e.preventDefault(); if (from && to && !bad) onChange({ kind: 'custom', from, to }); }}>
          <label style={{ fontSize: 13, color: L.muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{t('reports.period.from')}
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="period-from" style={{ height: 36, borderRadius: 8, border: `1px solid ${L.border}`, padding: '0 8px', font: 'inherit' }} />
          </label>
          <label style={{ fontSize: 13, color: L.muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{t('reports.period.to')}
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="period-to" style={{ height: 36, borderRadius: 8, border: `1px solid ${L.border}`, padding: '0 8px', font: 'inherit' }} />
          </label>
          <button type="submit" disabled={!from || !to || bad} data-testid="period-apply" style={{ ...pillStyle(false), height: 36, opacity: !from || !to || bad ? 0.5 : 1 }}>{t('reports.period.apply')}</button>
          {bad && <span role="alert" style={{ fontSize: 13, color: L.accent }}>{t('reports.period.bad')}</span>}
        </form>
      )}
    </div>
  );
}

// title = a heading in Leo's serif ("By company", "Invoice"); label = the small capitals over a figure ("DISCREPANCIES IDENTIFIED")
export function Card({ children, title = null, label = null, testid = null, pad = '22px 24px', style = null }) {
  return (
    <section data-testid={testid || undefined} style={{ background: L.card, border: `1px solid ${L.border}`, borderRadius: 20, padding: pad, boxSizing: 'border-box', minWidth: 0, ...style }}>
      {title && <h2 style={{ margin: '0 0 12px', fontFamily: L.serif, fontSize: 21, fontWeight: 500, color: L.text }}>{title}</h2>}
      {label && <h2 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 600, letterSpacing: 1.3, color: L.muted, textTransform: 'uppercase' }}>{label}</h2>}
      {children}
    </section>
  );
}

// The bars of a series, under a small title that says how they are grouped ("By month", as on the boards). Few buckets are all labelled; many are labelled every few bars. A bar's value is in
// its tooltip and in the accessible text.
export function BarChart({ series, grain, currency, color = L.accent, testid = 'report-chart' }) {
  const t = useT();
  const { lang } = useLang();
  const W = 640, H = 150, base = 120, top = 8;
  const max = Math.max(0, ...series.map((b) => b.value));
  const n = Math.max(1, series.length);
  const slot = W / n;
  const bw = Math.max(4, Math.min(48, slot * 0.62));
  const step = n > 14 ? Math.ceil(n / 8) : 1;
  const summary = series.map((b, i) => `${bucketLabel(b, grain, i, n, lang, t)} ${moneyText(b.value, currency)}`).join(', ');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
    <span data-testid="chart-title" style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1.3, color: L.muted, textTransform: 'uppercase' }}>{t(`reports.chart.by.${grain}`)}</span>
    <svg role="img" aria-label={summary} data-testid={testid} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 150, display: 'block' }}>
      <line x1="0" x2={W} y1={base} y2={base} stroke={L.border} />
      {series.map((b, i) => {
        const h = max > 0 ? Math.max(b.value > 0 ? 2 : 0, ((base - top) * b.value) / max) : 0;
        const x = i * slot + (slot - bw) / 2;
        return (
          <g key={b.key}>
            <rect x={x} y={base - h} width={bw} height={h} rx="3" fill={color}><title>{`${bucketLabel(b, grain, i, n, lang, t)}: ${symbol(currency)}${Math.round(b.value).toLocaleString('en-GB')}`}</title></rect>
            {i % step === 0 && <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize="12" fill={L.muted}>{bucketLabel(b, grain, i, n, lang, t)}</text>}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

// The big figure of a top band with its label and the sentence under it ("excluding 2 suppliers without a rate")
export function BigFigure({ label, value, sub = null, extra = null, color = L.text, testid = 'report-total' }) {
  const vk = useViewportKind();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1.3, color: L.muted, textTransform: 'uppercase' }}>{label}</span>
      <span data-testid={testid} style={{ fontFamily: L.serif, fontSize: vk === 'phone' ? 40 : 52, fontWeight: 600, lineHeight: 1.05, color }}>{value}</span>
      {sub && <span style={{ fontSize: 14, color: L.muted }}>{sub}</span>}
      {extra}
    </div>
  );
}

// "never silent": what the figures above rest on
export function Notes({ notes, extra = [], testid = 'report-notes' }) {
  const t = useT();
  const lines = [...noteLines(t, notes), ...extra.filter(Boolean)];
  if (!lines.length) return null;
  return (
    <ul data-testid={testid} style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: L.muted }}>
      {lines.map((l) => <li key={l}>{l}</li>)}
    </ul>
  );
}

export function Message({ status, onRetry, children = null }) {
  const t = useT();
  if (status === 'error') {
    return (
      <Card testid="report-error">
        <div role="alert" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, justifyContent: 'space-between' }}>
          <span style={{ fontSize: 16 }}>{t('reports.error')}</span>
          <button type="button" onClick={onRetry} data-testid="report-retry" style={{ height: 44, padding: '0 20px', borderRadius: 10, font: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer', background: L.accent, color: '#FFFFFF', border: 'none' }}>{t('reports.retry')}</button>
        </div>
      </Card>
    );
  }
  return <Card testid="report-loading"><span aria-busy="true" style={{ color: L.muted }}>{children || t('reports.loading')}</span></Card>;
}

// A row with a name, a bar and a figure (the rankings)
export function BarRow({ to = null, name, bar, figure, extra = null, testid = null }) {
  const inner = (
    <>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere', fontSize: 16 }}>{name}</span>
      <span aria-hidden="true" style={{ height: 8, borderRadius: 4, background: L.rowLine, position: 'relative', minWidth: 60 }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.max(0, Math.min(100, bar || 0))}%`, borderRadius: 4, background: L.accent }} />
      </span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 16, fontWeight: 600 }}>{figure}</span>
      {extra !== null && <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 16, color: L.muted }}>{extra}</span>}
    </>
  );
  const style = { display: 'grid', gridTemplateColumns: extra !== null ? 'minmax(0, 2fr) minmax(60px, 3fr) auto auto' : 'minmax(0, 2fr) minmax(60px, 3fr) auto', alignItems: 'center', gap: 14, padding: '10px 0', borderTop: `1px solid ${L.rowLine}`, color: L.text, textDecoration: 'none' };
  return to ? <Link to={to} data-testid={testid || undefined} style={style}>{inner}</Link> : <div data-testid={testid || undefined} style={style}>{inner}</div>;
}

// "N" + a label, in a list of problems
export function ListRow({ to = null, label, value, testid = null }) {
  const style = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderTop: `1px solid ${L.rowLine}`, color: L.text, textDecoration: 'none', fontSize: 16 };
  const inner = (<><span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{value}</span></>);
  return to ? <Link to={to} data-testid={testid || undefined} style={style}>{inner}</Link> : <div data-testid={testid || undefined} style={style}>{inner}</div>;
}

// "Late, not yet invoiced: N orders, amount" beside a forecast total (LEO-FEEDBACK G21): orders whose expected term end has passed are not in the forecast, and this says so.
export function LateLine({ late, currency, testid = 'forecast-late' }) {
  const t = useT();
  if (!late || !(late.orders > 0)) return null;
  return (
    <div data-testid={testid} title={t('reports.forecast.late.hint')} style={{ marginTop: 10, fontSize: 14, color: L.accent, fontWeight: 600 }}>
      {late.orders === 1 ? t('reports.forecast.late.one', { amount: moneyText(late.value, currency) }) : t('reports.forecast.late.many', { n: countText(late.orders), amount: moneyText(late.value, currency) })}
      {late.value_missing > 0 && <span style={{ display: 'block', fontWeight: 400, color: L.muted }}>{t('reports.forecast.late.missing', { n: countText(late.value_missing) })}</span>}
    </div>
  );
}

// An error row of the Discrepancies board: the name, the number of cases in grey, the amount in bold (empty where there is no money to add up)
export function StatRow({ to = null, label, count, amount = '', testid = null }) {
  const style = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', alignItems: 'baseline', gap: 14, padding: '8px 0', borderTop: `1px solid ${L.rowLine}`, color: L.text, textDecoration: 'none', fontSize: 16 };
  const inner = (
    <>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
      <span data-testid={testid ? `${testid}-count` : undefined} style={{ fontSize: 14, color: L.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{count}</span>
      <span data-testid={testid ? `${testid}-amount` : undefined} style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right', minWidth: 72 }}>{amount}</span>
    </>
  );
  return to ? <Link to={to} data-testid={testid || undefined} style={style}>{inner}</Link> : <div data-testid={testid || undefined} style={style}>{inner}</div>;
}
